import api from './api';

export interface ReviewRequest {
  revieweeId: string;
  overallRating: number;
  title?: string;
  reviewText?: string;
  communicationRating?: number;
  expertiseRating?: number;
  responsivenessRating?: number;
  valueRating?: number;
  researchQualityRating?: number;
  accuracyRating?: number;
  actionableInsightsRating?: number;
  serviceType?: string;
  engagementDuration?: string;
  wouldRecommend?: boolean;
}

export interface ReviewResponse {
  id: string;
  reviewerId: string;
  reviewerName: string;
  reviewerProfilePicture?: string;
  revieweeId: string;
  revieweeName: string;
  overallRating: number;
  title?: string;
  reviewText?: string;
  communicationRating?: number;
  expertiseRating?: number;
  responsivenessRating?: number;
  valueRating?: number;
  researchQualityRating?: number;
  accuracyRating?: number;
  actionableInsightsRating?: number;
  serviceType?: string;
  engagementDuration?: string;
  wouldRecommend?: boolean;
  status: string;
  responseText?: string;
  responseDate?: string;
  isVerified?: boolean;
  isFeatured?: boolean;
  helpfulCount: number;
  isHelpful?: boolean;
  createdAt: string;
}

export interface ReviewSummary {
  userId: string;
  userName: string;
  userRole: string;
  averageRating: number | null;
  totalReviews: number;
  approvedReviews: number;
  averageCommunicationRating: number | null;
  averageExpertiseRating: number | null;
  averageResponsivenessRating: number | null;
  averageValueRating: number | null;
  averageResearchQualityRating: number | null;
  averageAccuracyRating: number | null;
  averageActionableInsightsRating: number | null;
  ratingDistribution: Record<number, number>;
  recommendationPercentage: number | null;
  recentReviewsCount: number;
}

export interface PaginatedReviews {
  reviews: ReviewResponse[];
  totalReviews: number;
  page: number;
  size: number;
  hasMore: boolean;
}

const reviewService = {
  async createReview(data: ReviewRequest): Promise<ReviewResponse> {
    return api.post('/reviews', data);
  },

  async updateReview(reviewId: string, data: ReviewRequest): Promise<ReviewResponse> {
    return api.put(`/reviews/${reviewId}`, data);
  },

  async deleteReview(reviewId: string): Promise<void> {
    return api.delete(`/reviews/${reviewId}`);
  },

  async getReviewSummary(userId: string): Promise<ReviewSummary> {
    return api.get(`/reviews/summary/${userId}`);
  },

  async getUserReviews(revieweeId: string, params?: {
    page?: number; size?: number; sortBy?: string; sortDir?: string; rating?: number;
  }): Promise<PaginatedReviews> {
    const query = new URLSearchParams();
    if (params) {
      if (params.page !== undefined) query.set('page', String(params.page));
      if (params.size !== undefined) query.set('size', String(params.size));
      if (params.sortBy) query.set('sortBy', params.sortBy);
      if (params.sortDir) query.set('sortDir', params.sortDir);
      if (params.rating !== undefined) query.set('rating', String(params.rating));
    }
    return api.get(`/reviews/user/${revieweeId}?${query}`);
  },

  async voteHelpful(reviewId: string, isHelpful: boolean): Promise<ReviewResponse> {
    return api.post(`/reviews/${reviewId}/vote`, { isHelpful });
  },

  async removeVote(reviewId: string): Promise<void> {
    return api.delete(`/reviews/${reviewId}/vote`);
  },

  async flagReview(reviewId: string): Promise<void> {
    return api.post(`/reviews/${reviewId}/flag`);
  },

  async addResponse(reviewId: string, responseText: string): Promise<ReviewResponse> {
    return api.post(`/reviews/${reviewId}/response`, { responseText });
  },
};

export default reviewService;
